// Story 1.28 — admin portfolio-applications queue with portfolio-
// grouping + per-portfolio multi-select bulk-approve UI. Wrapped by
// <AdminGuard> in App.tsx.
//
// Mirrors Story 1.27's AdminApplicationsPage shell. Differences:
//   - Rows are grouped client-side by portfolio_id (the response is
//     already pre-ordered by (portfolio_id, submitted_at ASC)).
//   - Each section renders a per-portfolio bulk-approve toolbar
//     (multi-select + "Approve selected (N)").
//   - Per-row actions: Approve (row-level) + a deep-link to the detail
//     page (which carries the Reject UI).

import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link, useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@greenhold/components";

import { EmptyState } from "../components/EmptyState";
import {
  type AdminPortfolioApplicationsFilters,
  type AdminPortfolioApplicationsListRow,
  type AdminPortfolioApplicationStatus,
  DEFAULT_PORTFOLIO_APPLICATIONS_FILTERS,
  useAdminPortfolioApplications,
} from "../features/admin/useAdminPortfolioApplications";
import {
  useApprovePortfolioApplicationMutation,
  useBulkApprovePortfolioApplicationsMutation,
} from "../features/admin/useAdminPortfolioApplicationMutations";

function statusBadgeVariant(
  status: "pending" | "approved" | "rejected" | "withdrawn",
): "warning" | "success" | "destructive" | "secondary" {
  switch (status) {
    case "pending":
      return "warning";
    case "approved":
      return "success";
    case "rejected":
      return "destructive";
    case "withdrawn":
      return "secondary";
  }
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function filtersFromSearchParams(
  params: URLSearchParams,
): AdminPortfolioApplicationsFilters {
  const status =
    (params.get("status") as AdminPortfolioApplicationStatus | null) ??
    "pending";
  return {
    status,
    portfolio_id: params.get("portfolio_id") ?? undefined,
    submitted_since: params.get("submitted_since") ?? undefined,
    submitted_until: params.get("submitted_until") ?? undefined,
    display_name_search: params.get("display_name_search") ?? undefined,
    page: Number(params.get("page") ?? "1") || 1,
    page_size:
      Number(
        params.get("page_size") ??
          String(DEFAULT_PORTFOLIO_APPLICATIONS_FILTERS.page_size),
      ) || DEFAULT_PORTFOLIO_APPLICATIONS_FILTERS.page_size,
  };
}

export function AdminPortfolioApplicationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(
    () => filtersFromSearchParams(searchParams),
    [searchParams],
  );

  const query = useAdminPortfolioApplications(filters);

  function updateFilter<K extends keyof AdminPortfolioApplicationsFilters>(
    key: K,
    value: AdminPortfolioApplicationsFilters[K] | undefined,
  ) {
    const next = new URLSearchParams(searchParams);
    if (value === undefined || value === "" || value === null) {
      next.delete(key);
    } else {
      next.set(key, String(value));
    }
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  }

  function goToPage(page: number) {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(Math.max(1, page)));
    setSearchParams(next);
  }

  return (
    <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900 p-6">
      <div className="max-w-6xl mx-auto space-y-[var(--gh-rhythm-admin,16px)]">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold">
              Portfolio applications
            </h1>
            <p className="opacity-70 text-sm">
              Review and decide pending applications. Grouped by portfolio
              so you can sweep all applicants to one drop in a single pass.
            </p>
          </div>
          {query.data?.total_pending !== null &&
            query.data?.total_pending !== undefined && (
              <Badge variant="warning">
                {query.data.total_pending} pending
              </Badge>
            )}
        </header>

        {/* Filter bar */}
        <Card>
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
            <label className="text-sm">
              <span className="block opacity-70 mb-1">Status</span>
              <select
                value={filters.status}
                onChange={(e) =>
                  updateFilter(
                    "status",
                    e.target.value as AdminPortfolioApplicationStatus,
                  )
                }
                className="w-full rounded border px-2 py-1 bg-brand-paper-100"
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="withdrawn">Withdrawn</option>
                <option value="any">Any</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="block opacity-70 mb-1">Creator search</span>
              <input
                type="text"
                value={filters.display_name_search ?? ""}
                placeholder="display name…"
                onChange={(e) =>
                  updateFilter(
                    "display_name_search",
                    e.target.value || undefined,
                  )
                }
                className="w-full rounded border px-2 py-1 bg-brand-paper-100"
              />
            </label>
            <label className="text-sm">
              <span className="block opacity-70 mb-1">Portfolio ID filter</span>
              <input
                type="text"
                value={filters.portfolio_id ?? ""}
                placeholder="UUID (optional)"
                onChange={(e) =>
                  updateFilter("portfolio_id", e.target.value || undefined)
                }
                className="w-full rounded border px-2 py-1 bg-brand-paper-100 font-mono text-xs"
              />
            </label>
          </CardContent>
        </Card>

        {/* Body */}
        {query.isPending && <p className="opacity-70">Loading queue…</p>}
        {query.isError && (
          <Card>
            <CardContent className="p-6 space-y-3">
              <p className="text-destructive">
                Couldn&apos;t load the queue. {query.error.message}
              </p>
              <Button
                variant="secondary"
                onClick={() => query.refetch()}
              >
                Retry
              </Button>
            </CardContent>
          </Card>
        )}
        {query.data && query.data.data.length === 0 && (
          <EmptyState
            aria-label="No portfolio applications"
            description="Queue's clear — every portfolio application has been reviewed."
          />
        )}
        {query.data && query.data.data.length > 0 && (
          <PortfolioGroups rows={query.data.data} />
        )}

        {/* Pagination */}
        {query.data && (
          <div className="flex items-center justify-between text-sm opacity-80">
            <span>
              Page {query.data.page} · {query.data.data.length} rows
            </span>
            <div className="flex gap-2">
              <Button
                variant="tertiary"
                size="sm"
                disabled={query.data.page <= 1}
                onClick={() => goToPage(query.data!.page - 1)}
              >
                Prev
              </Button>
              <Button
                variant="tertiary"
                size="sm"
                disabled={!query.data.has_next}
                onClick={() => goToPage(query.data!.page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

// ====================================================================
// Grouped portfolio sections with multi-select bulk-approve
// ====================================================================

function PortfolioGroups({
  rows,
}: {
  rows: AdminPortfolioApplicationsListRow[];
}) {
  const groups = useMemo(() => {
    const map = new Map<
      string,
      { portfolio_title: string; rows: AdminPortfolioApplicationsListRow[] }
    >();
    for (const row of rows) {
      if (!map.has(row.portfolio_id)) {
        map.set(row.portfolio_id, {
          portfolio_title: row.portfolio_title,
          rows: [],
        });
      }
      map.get(row.portfolio_id)!.rows.push(row);
    }
    return Array.from(map.entries());
  }, [rows]);

  return (
    <div className="space-y-6">
      {groups.map(([portfolioId, group]) => (
        <PortfolioSection
          key={portfolioId}
          portfolioId={portfolioId}
          portfolioTitle={group.portfolio_title}
          rows={group.rows}
        />
      ))}
    </div>
  );
}

function PortfolioSection({
  portfolioId,
  portfolioTitle,
  rows,
}: {
  portfolioId: string;
  portfolioTitle: string;
  rows: AdminPortfolioApplicationsListRow[];
}) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const bulkApprove = useBulkApprovePortfolioApplicationsMutation(portfolioId);

  const pendingRows = rows.filter((r) => r.status === "pending");
  const pendingIds = pendingRows.map((r) => r.portfolio_application_id);

  function toggle(id: string) {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  }

  function toggleAll() {
    if (pendingIds.every((id) => selectedIds.has(id))) {
      // Currently all selected → clear.
      setSelectedIds(new Set());
    } else {
      // Otherwise select all pending in this section.
      setSelectedIds(new Set(pendingIds));
    }
  }

  async function handleBulkApprove() {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const result = await bulkApprove.mutateAsync(ids);
      const success = result.data.successes.length;
      const fail = result.data.failures.length;
      if (fail === 0) {
        toast.success(`Approved ${success} application${success === 1 ? "" : "s"}.`);
      } else {
        toast(
          `Approved ${success}, ${fail} failed (see per-row markers).`,
          { icon: "⚠️" },
        );
      }
      setSelectedIds(new Set());
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Bulk approve failed.";
      toast.error(msg);
    }
  }

  const allPendingSelected =
    pendingIds.length > 0 && pendingIds.every((id) => selectedIds.has(id));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <CardTitle>
            {portfolioTitle}
            <Badge variant="secondary" className="ml-2 font-mono">
              {pendingRows.length} pending
            </Badge>
          </CardTitle>
          {pendingRows.length > 0 && (
            <div className="flex items-center gap-3">
              <label className="text-sm flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={allPendingSelected}
                  onChange={toggleAll}
                  aria-label={`Select all pending applications for ${portfolioTitle}`}
                />
                Select all
              </label>
              <Button
                variant="primary"
                size="sm"
                disabled={selectedIds.size === 0 || bulkApprove.isPending}
                onClick={handleBulkApprove}
              >
                {bulkApprove.isPending
                  ? "Approving…"
                  : `Approve selected (${selectedIds.size})`}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {rows.map((row) => (
          <ApplicationRow
            key={row.portfolio_application_id}
            row={row}
            selected={selectedIds.has(row.portfolio_application_id)}
            onToggle={() => toggle(row.portfolio_application_id)}
            portfolioId={portfolioId}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function ApplicationRow({
  row,
  selected,
  onToggle,
  portfolioId,
}: {
  row: AdminPortfolioApplicationsListRow;
  selected: boolean;
  onToggle: () => void;
  portfolioId: string;
}) {
  const approve = useApprovePortfolioApplicationMutation(
    row.portfolio_application_id,
  );

  const isPending = row.status === "pending";

  async function handleApprove() {
    try {
      await approve.mutateAsync();
      toast.success(
        `Approved ${row.creator_display_name ?? "creator"} for this portfolio.`,
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Approve failed.";
      toast.error(msg);
    }
  }

  return (
    <div
      className="flex items-center gap-3 border rounded p-3 bg-brand-paper-100"
      id={`portfolio-application-${row.portfolio_application_id}`}
    >
      {isPending && (
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Select application from ${row.creator_display_name ?? "creator"}`}
        />
      )}
      <div className="flex-1 min-w-0">
        <p className="font-medium">
          {row.creator_display_name ?? "(no display name)"}
          {row.creator_handle && (
            <span className="opacity-60 ml-2 font-mono text-xs">
              @{row.creator_handle}
            </span>
          )}
        </p>
        {row.effective_hello_text && (
          <p className="text-sm opacity-80 truncate">
            {row.effective_hello_text}
          </p>
        )}
        <p className="text-xs opacity-60 mt-1">
          Submitted {formatRelative(row.submitted_at)}
        </p>
      </div>
      <Badge variant={statusBadgeVariant(row.status)}>{row.status}</Badge>
      <div className="flex items-center gap-2">
        <Link
          to={`/admin/portfolio-applications/${row.portfolio_application_id}`}
          className="text-sm underline decoration-dotted"
        >
          Details
        </Link>
        {isPending && (
          <Button
            variant="primary"
            size="sm"
            disabled={approve.isPending}
            onClick={handleApprove}
          >
            {approve.isPending ? "…" : "Approve"}
          </Button>
        )}
      </div>
      {/* keep portfolioId in scope so the section can reference it */}
      <span className="hidden" data-portfolio-id={portfolioId} />
    </div>
  );
}
